import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/feature/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Reveal from "@/components/base/Reveal";
import { news as newsData } from "@/mocks/news";
import type { NewsItem } from "@/types/content";
import { formatDate } from "@/utils/format";

const news = newsData as NewsItem[];
const PER_PAGE = 6;

export default function News() {
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...news].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <title>奥田咲最新动态 - 影视资讯与作品更新</title>
      <meta
        name="description"
        content="奥田咲最新动态资讯聚合页，涵盖新作发布、拍摄花絮、活动出席、媒体访谈等内容，实时了解奥田咲的影视工作进展。"
      />
      <meta name="keywords" content="奥田咲动态,奥田咲最新动态,奥田咲新闻,奥田咲资讯" />

      <SiteHeader />
      <PageHeader
        eyebrow="LATEST NEWS"
        title="最新动态"
        description="汇集奥田咲相关的影视资讯与工作动态，包括新作发布、拍摄花絮、活动出席与媒体访谈，按发布时间倒序排列。"
        crumbs={[{ label: "首页", to: "/" }, { label: "最新动态" }]}
        extra={
          <div className="text-center">
            <p className="font-heading text-3xl font-bold text-accent-400">{news.length}</p>
            <p className="mt-1 text-xs text-background-400">条动态资讯</p>
          </div>
        }
      />

      <main className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="w-full max-w-5xl mx-auto">
          {paged.length > 0 ? (
            <>
              <div className="flex flex-col gap-4">
                {paged.map((item, idx) => (
                  <Reveal key={item.id} delay={idx * 60}>
                    <Link
                      to={`/news/${item.id}`}
                      className="group grid grid-cols-1 sm:grid-cols-12 gap-5 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="sm:col-span-4 lg:col-span-3">
                        <div className="w-full aspect-[16/10] rounded-md overflow-hidden bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} - 奥田咲动态配图`}
                            title={`${item.title} 奥田咲动态`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-8 lg:col-span-9 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <span className="px-2.5 py-0.5 rounded bg-accent-100 text-accent-800 font-medium">
                            {item.category}
                          </span>
                          <span className="flex items-center gap-1 text-foreground-500">
                            <i className="ri-calendar-line"></i>
                            {formatDate(item.date)}
                          </span>
                        </div>
                        <h2 className="mt-3 font-heading text-lg md:text-xl font-bold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </h2>
                        <p className="mt-2.5 text-sm text-foreground-600 leading-7 line-clamp-2 md:line-clamp-3">
                          {item.summary}
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-sm text-primary-700 font-medium">
                          阅读全文
                          <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-newspaper-line"
              title="暂无动态消息"
              description="目前还没有发布新的动态资讯，请稍后再来看看，或先浏览奥田咲的影视作品。"
              actionLabel="浏览影视作品"
              to="/works"
            />
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}